import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { Spinner } from "@/components/ui/spinner"

// Plate & Hairline §02.1 (Button) + §17.1/§17.4 (the radius rule + the pill
// and the rectangle): a button is PRESSED, so its radius is always
// --ls-radius-pill (999px — scales to half the shorter side; a square icon
// button becomes a true circle for free, which is --ls-radius-round derived
// the same way — no separate "icon" radius is needed). There is no
// radius-sm/md step to land on at any size.
//
// Back-compat resolution for the six-vs-five variant question (§02.1: "the
// first draft said six, counting a variant the implementation never
// branched on"): the CVA API keeps all six existing names — default,
// destructive, outline, secondary, ghost, link — because ~100+ call sites
// already depend on them and this is a foundation-layer pass, not a
// call-site migration. They resolve onto the spec's five (primary,
// secondary, ghost, destructive, icon) as follows:
//   default      → primary    (accent fill)
//   outline      → secondary  (1px ink border, transparent, inverts on press)
//   secondary    → secondary  (alias of outline — literally the same class
//                  string below, kept only so existing "secondary" call
//                  sites don't need a rename)
//   ghost        → ghost      (unchanged)
//   destructive  → destructive (corrected below: the spec's destructive is
//                  transparent + 1px ink border + underline, NOT a red fill
//                  — this system has no red, no second accent, no semantic
//                  colour per PRODUCT.md's colour section. This is a visible
//                  change to every existing destructive button call site;
//                  flagged in the wave report rather than silently shipped.)
//   link         → not a spec primitive (prose/inline text style, kept as
//                  its own variant because removing it breaks call sites)
//   icon         → a SIZE, not a variant, in this implementation. A square
//                  44×44 box at radius-pill self-corrects to a true circle
//                  by the §17.4 pill formula, so "icon" only needs its own
//                  size token, not a fourth radius or a separate variant
//                  branch. Pair size="icon" with variant="outline" (or
//                  "ghost") for the spec's bordered/borderless icon button.
// §12.3 — press is 80ms, linear, compositor-only (transform + opacity), and
// deliberately NOT the same timing as the button's color/border state
// transition (--motion-duration-standard, 200ms cubic-bezier). A single
// arbitrary `transition` shorthand (not two `transition-[...]` utilities,
// which don't merge — Tailwind emits one `transition-property` declaration
// per class and the later one in the sheet wins outright) keeps color/
// border on their existing curve untouched while the press itself gets the
// spec's own token. `active:` is the CSS-only proxy for `pointerdown`
// available without a JS press handler (hooks/** is out of this wave's
// scope) — it holds while the pointer is down and releases on drag-off in
// every evergreen engine, which is the behavior §12.3 asks for.
//
// §12.10 reduced-motion row for "Press feedback": "No transform. Background
// darkens instantly instead — press is still confirmed." `motion-safe:`
// on the scale/opacity utilities above already withholds them under
// prefers-reduced-motion, but nothing filled the gap left behind — a
// reduced-motion user got zero visual confirmation their press registered.
// Each variant below adds a `motion-reduce:active:` background (and, for the
// two invert-on-hover variants, text colour) that reuses the SAME target the
// variant already defines for `:hover:`, so press reads as "the hover state,
// but instant" rather than inventing a second colour. The global reduced-
// motion override disables CSS transitions, so the change lands immediately.
const buttonVariants = cva(
  "relative inline-flex max-w-full items-center justify-center gap-2 text-balance rounded-[var(--ls-radius-pill)] border font-medium [transition:background-color_var(--motion-duration-standard)_var(--motion-ease-standard),border-color_var(--motion-duration-standard)_var(--motion-ease-standard),color_var(--motion-duration-standard)_var(--motion-ease-standard),box-shadow_var(--motion-duration-standard)_var(--motion-ease-standard),scale_var(--ls-motion-instant)_var(--ls-ease-instant),opacity_var(--ls-motion-instant)_var(--ls-ease-instant)] motion-safe:active:scale-[0.97] motion-safe:active:opacity-85 disabled:pointer-events-none disabled:opacity-40 aria-busy:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none tlsr-focus aria-invalid:border-danger",
  {
    variants: {
      variant: {
        default:
          // DS §02.1: primary is weight 700 (the one accent-filled control per panel).
          "border-transparent bg-brand text-brand-foreground shadow-none hover:bg-brand-hover motion-reduce:active:bg-brand-hover font-bold",
        // §02.1 destructive: transparent, 1px ink border, underline — text
        // treatment, not a colour treatment. See back-compat note above.
        destructive:
          "border-[color:var(--tls-fg-strong)] bg-transparent text-[var(--tls-fg-strong)] underline underline-offset-4 shadow-none hover:bg-surface-hover motion-reduce:active:bg-surface-hover font-semibold",
        outline:
          "border-[color:var(--tls-fg-strong)] bg-transparent text-[var(--tls-fg-strong)] shadow-none hover:bg-[var(--tls-fg-strong)] hover:text-[var(--tls-canvas)] motion-reduce:active:bg-[var(--tls-fg-strong)] motion-reduce:active:text-[var(--tls-canvas)]",
        secondary:
          "border-[color:var(--tls-fg-strong)] bg-transparent text-[var(--tls-fg-strong)] shadow-none hover:bg-[var(--tls-fg-strong)] hover:text-[var(--tls-canvas)] motion-reduce:active:bg-[var(--tls-fg-strong)] motion-reduce:active:text-[var(--tls-canvas)]",
        ghost:
          "border-transparent bg-transparent text-[var(--tls-fg-muted)] shadow-none hover:bg-surface-hover hover:text-[var(--tls-fg-strong)] motion-reduce:active:bg-surface-hover motion-reduce:active:text-[var(--tls-fg-strong)]",
        // text-brand (--tls-brand, the FILL value) is 3.21:1 on canvas —
        // fine for a 3:1 icon, a fail for 4.5:1 text. Link is text, so it
        // uses the accent-as-text token instead (§01.2's six-role rule,
        // role 6: links). See app/globals.css's .tls-brand-text.
        link: "border-transparent bg-transparent px-0 text-[var(--tls-brand-ink)] underline-offset-4 shadow-none hover:underline hover:text-[var(--tls-brand-ink-hover)] rounded-none",
      },
      // §02.1 measurements: height 44/36/52, padding 12/32 md · 8/20 sm ·
      // 16/40 lg, type 14/12.5/16. has-[>svg] narrows the horizontal padding
      // when a leading/trailing icon already supplies visual weight, same as
      // before — only the base numbers moved to match the spec exactly.
      //
      // `min-h-[var(--touch-target-min)]` used to sit in the base class
      // unconditionally, which beat `sm`'s `h-9` (min-height always wins
      // over height) and painted every `sm` control at 44px instead of the
      // spec's 36 — an 8px vertical-rhythm shift on every sm site. default/
      // lg/icon are naturally ≥44 already (44/52/44), so removing the
      // blanket rule doesn't change them. `sm` restores its own 44px hit
      // area via the `before:` pseudo-element below instead — the spec's
      // own words for this are "Pill height 36 · hit area padded to 44
      // vertically" (§02.4): the target is padded, not the paint.
      size: {
        default: "h-11 min-w-[132px] px-8 py-3 text-sm has-[>svg]:px-6 has-[>svg]:min-w-0",
        // §17.4 (the pill and the rectangle): a pill narrower than it is
        // tall is always a bug — the min-width isn't cosmetic, it's what
        // keeps the 999px radius reading as a stadium instead of degrading
        // into a circle then a vertical stadium as content shrinks. The
        // previous pass only set this on default/lg; sm needs the same
        // floor at its own proportion (132 : 44 height :: 108 : 36 height).
        //
        // The `before:` pseudo-element extends the clickable/tappable box
        // above and below the painted 36px pill without adding a wrapper
        // element or changing the painted box any call site measures. A
        // click/tap anywhere in the pseudo-element's box still fires the
        // button's own handler, because the pseudo-element is part of the
        // button's rendering subtree. 5px, not 4: an absolutely positioned
        // pseudo-element is inset from the PADDING box, and the button has a
        // 1px border, so -4px each side measured a 42px hit box (34 + 8);
        // 5px each side is what makes it 44 (34 + 10). Measured with
        // elementFromPoint on a coarse pointer at 390.
        // §15.2/§15.7: `text-[0.78125rem]` was a raw px arbitrary value — the
        // one size tier here that didn't use a Tailwind rem-based text-*
        // utility (default/lg already use text-sm/text-base), so it was
        // invisible to OS/browser text-size scaling. 0.78125rem = 12.5px
        // at the 16px reference root, byte-identical at 100% text size.
        sm: "relative h-9 min-w-[108px] gap-1.5 px-5 py-2 text-[0.78125rem] has-[>svg]:px-4 has-[>svg]:min-w-0 before:absolute before:-inset-y-[5px] before:inset-x-0 before:content-['']",
        lg: "h-[52px] min-w-[132px] px-10 py-4 text-base has-[>svg]:px-7 has-[>svg]:min-w-0",
        icon: "size-11 min-w-0 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    /**
     * §02.1 loading state: swaps the leading slot for a spinner, sets
     * aria-busy so the accessible name stays stable while the label text is
     * free to change (e.g. "Preparing file…"), and disables interaction
     * without touching the disabled prop (aria-busy, not aria-disabled —
     * disabling the primary as a validation strategy is the §02.1 DO NOT).
     */
    loading?: boolean
  }

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button"

  // §02.1 icon-only aria pattern: an icon-only button (size="icon") with no
  // visible text label must carry an accessible name via aria-label. This
  // only asserts in development so it never affects the production bundle.
  if (process.env.NODE_ENV !== "production" && size === "icon" && !asChild) {
    const hasAccessibleName =
      Boolean((props as { "aria-label"?: string })["aria-label"]) ||
      Boolean((props as { "aria-labelledby"?: string })["aria-labelledby"])
    if (!hasAccessibleName) {
      // eslint-disable-next-line no-console
      console.warn(
        "[Button] size=\"icon\" requires aria-label (or aria-labelledby) naming the action and its object, e.g. \"Play Windows XP Shutdown\" (§02.1)."
      )
    }
  }

  // Radix Slot (asChild) requires exactly one React-element child to clone
  // props onto — `{loading && <Spinner/>}{children}` is two array slots even
  // when the first is null, which throws "React.Children.only". Collapsing
  // to a single conditional expression keeps Comp's children at exactly one
  // node in both branches.
  const content = loading ? (
    <>
      {/* aria-hidden: the button's own aria-busy is the accessible signal
          (§02.1); the spinner's default role="status" would announce a
          second, redundant "Loading". */}
      <Spinner size="xs" aria-hidden="true" className="text-current" />
      {children}
    </>
  ) : (
    children
  )

  return (
    <Comp
      data-slot="button"
      aria-busy={loading || undefined}
      disabled={asChild ? undefined : disabled || loading}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {content}
    </Comp>
  )
}

// eslint-disable-next-line react-doctor/only-export-components
export { Button, buttonVariants }
export type { ButtonProps }
